import { initializeApp } from 'firebase/app';
import { getFirebaseConfig } from './firebase-config.js';
import DeviceDetector from 'node-device-detector';
import DeviceHelper from 'node-device-detector/helper';
import qrcode from 'qrcode-generator';

// Initialize device detector
const detector = new DeviceDetector({
  clientIndexes: true,
  deviceIndexes: true,
  osIndexes: true,
  deviceAliasCode: false,
  deviceTrusted: false,
  deviceInfo: false,
  maxUserAgentSize: 500,
});

// DOM elements
let loadingScreen;
let mainContent;
let errorScreen;
let errorMessage;
let retryButton;
let contentDiv;
let mobileOnlyScreen;
let qrCodeContainer;
let currentUrlElement;
let getAppScreen;
let getAppButton;
let getAppDescription;

// Firebase instance
let app;

/**
 * Initialize Firebase with the appropriate configuration
 */
function initializeFirebase() {
  const config = getFirebaseConfig();
  
  // Initialize Firebase app
  app = initializeApp(config);
}

/**
 * Show loading screen
 */
function showLoading() {
  loadingScreen.classList.remove('fade-out');
  mainContent.classList.add('hidden');
  errorScreen.classList.add('hidden');
}

/**
 * Hide loading screen with fade effect
 */
function hideLoading() {
  loadingScreen.classList.add('fade-out');
  setTimeout(() => {
    loadingScreen.style.display = 'none';
  }, 300);
}

/**
 * Show main content with data
 */
function showContent(data) {
  hideLoading();
  mainContent.classList.remove('hidden');
  
  // Display the response data
  if (data) {
    contentDiv.innerHTML = `
      <div class="response-data">
        <h3>Response from Firebase Function:</h3>
        <pre>${JSON.stringify(data, null, 2)}</pre>
      </div>
    `;
  }
}

/**
 * Show error screen
 */
function showError(error) {
  hideLoading();
  errorScreen.classList.remove('hidden');
  errorMessage.textContent = error.message || 'An unexpected error occurred';
  console.error('Error calling Firebase function:', error);
}

/**
 * Show the "Get the App" screen with customized message
 * @param {Object} data - Response data from Firebase containing redirect info
 */
function showGetAppScreen(data) {
  hideLoading();

  // Hide other screens
  mainContent.classList.add('hidden');
  errorScreen.classList.add('hidden');

  // Show get app screen
  getAppScreen.classList.remove('hidden');

  // Customize description based on Private Relay status
  if (data.isPrivateRelay) {
    getAppDescription.innerHTML = `
      To experience this TimeWalk, you'll need the Gazooky TimeWalks app.<br><br>
      <strong>Please follow these steps:</strong><br>
      1. Tap "Download App" below to open the App Store<br>
      2. After installing, open the Gazooky TimeWalks app<br>
      3. You will be asked to paste the content to launch your experience
    `;
  } else {
    getAppDescription.innerHTML = `
      To experience this TimeWalk, you'll need the Gazooky TimeWalks app.<br><br>
      Tap the button below to download the app. Once installed, the app will automatically open with your TimeWalk ready to explore!
    `;
  }

  // Store the data for button click
  getAppButton.onclick = async () => {
    if (data.isPrivateRelay) {
      try {
        // Copy to clipboard for Private Relay users
        const urlToCopy = window.location.href;
        console.log('URL to copy:', urlToCopy);
        console.log('URL type:', typeof urlToCopy);
        console.log('URL value:', String(urlToCopy));

        await navigator.clipboard.writeText(urlToCopy);
        console.log('Successfully copied to clipboard');

        // Redirect to App Store
        window.location.href = data.appStoreUrl;
      } catch (clipboardError) {
        console.error('Failed to copy to clipboard:', clipboardError);
        // Still redirect even if clipboard fails
        window.location.href = data.appStoreUrl;
      }
    } else {
      // Normal flow - redirect to appropriate store
      window.location.href = data.redirectUrl;
    }
  };
}

/**
 * Extract URL parameters from the query string
 */
function getURLParameters() {
  const params = new URLSearchParams(window.location.search);
  return {
    clientId: params.get('clt'),
    projectId: params.get('pro'),
    experienceId: params.get('exp'),
    relay: params.get('relay')
  };
}

/**
 * Validate that all required URL parameters are present
 */
function validateURLParameters(params) {
  return params.clientId && params.projectId && params.experienceId;
}

/**
 * Collects device signals for fingerprinting
 * @returns {Promise<Object>} Device signals object
 */
async function collectDeviceSignals() {
  const baseSignals = {
    pixelRatio: window.devicePixelRatio,
    timezoneOffset: new Date().getTimezoneOffset(),
    deviceModel: "",
    platformVersion: ""
  };

  // Try to get high-entropy values from Client Hints
  if (navigator.userAgentData) {
    try {
      const hints = await navigator.userAgentData.getHighEntropyValues([
        'model',
        'platformVersion'
      ]);
      
      baseSignals.deviceModel = hints.model || "";
      baseSignals.platformVersion = hints.platformVersion || "";
        
    } catch (e) {
      console.warn('Could not get client hints:', e);
    }
  }

  return baseSignals;
}


/**
 * Call Firebase Cloud Function
 * Extracts URL parameters and passes them to the generateDeepLink function
 */
async function callFirebaseFunction() {
  try {
    showLoading();
    
    // Extract URL parameters
    const params = getURLParameters();
    
    // Validate parameters
    if (!validateURLParameters(params)) {
      const error = new Error(
        'Welcome to Gazooky TimeWalks! Unfortunately, your link is invalid to launch an experience. Please contact your experience provider for more information.'
      );
      showError(error);
      return;
    }
    
    // Collect device signals for fingerprinting
    const deviceSignals = await collectDeviceSignals();

    // Get the Firebase function URL
    const config = getFirebaseConfig();
    const functionUrl = config.useEmulator
      ? `http://${config.functionsEmulatorHost}/${config.projectId}/europe-west2/generateDeepLink`
      : `https://europe-west2-${config.projectId}.cloudfunctions.net/generateDeepLink`;

    // Call the Firebase HTTP function
    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        clientId: params.clientId,
        projectId: params.projectId,
        experienceId: params.experienceId,
        deviceSignals: deviceSignals,
        relay: params.relay
      })
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();

    // Check if this is a successful response with redirect/Private Relay handling
    if (result.success && result.data) {
      if (result.data.isPrivateRelay || result.data.redirectUrl) {
        // Show the "Get the App" screen instead of auto-redirecting
        showGetAppScreen(result.data);
      } else {
        // Show the result for debugging
        showContent(result.data || result);
      }
    } else {
      // Show the result for debugging
      showContent(result.data || result);
    }
    
  } catch (error) {
    showError(error);
  }
}

/**
 * Check if device is mobile (iOS or Android)
 * If not, show QR code screen
 */
function checkDeviceAndShowQR() {
  // Detect device from user agent
  const userAgent = navigator.userAgent;
  const result = detector.detect(userAgent);
  
  // Check if the device is mobile using DeviceHelper
  const isMobileDevice = DeviceHelper.isMobile(result);
  
  if (!isMobileDevice) {
    // Device is not mobile, show the mobile-only screen with QR code
    showMobileOnlyScreen();
    return false; // Not a mobile device
  }
  
  return true; // Is a mobile device
}

/**
 * Generate QR code for the current URL
 */
function generateQRCode() {
  const currentUrl = window.location.href;
  
  // Create QR code
  const qr = qrcode(0, 'M'); // Type 0 (auto), Error correction level M
  qr.addData(currentUrl);
  qr.make();
  
  // Generate the QR code as HTML
  const qrHTML = qr.createImgTag(6, 8); // cell size 6, margin 8
  
  return { qrHTML, currentUrl };
}

/**
 * Show mobile-only screen with QR code
 */
function showMobileOnlyScreen() {
  // Hide other screens
  loadingScreen.style.display = 'none';
  mainContent.classList.add('hidden');
  errorScreen.classList.add('hidden');
  
  // Show mobile-only screen
  mobileOnlyScreen.classList.remove('hidden');
  
  // Generate and display QR code
  const { qrHTML, currentUrl } = generateQRCode();
  qrCodeContainer.innerHTML = qrHTML;
  currentUrlElement.textContent = currentUrl;
}

/**
 * Initialize the application
 */
function init() {
  // Get DOM elements
  loadingScreen = document.getElementById('loading-screen');
  mainContent = document.getElementById('main-content');
  errorScreen = document.getElementById('error-screen');
  errorMessage = document.getElementById('error-message');
  retryButton = document.getElementById('retry-button');
  contentDiv = document.getElementById('content');
  mobileOnlyScreen = document.getElementById('mobile-only-screen');
  qrCodeContainer = document.getElementById('qr-code-container');
  currentUrlElement = document.getElementById('current-url');
  getAppScreen = document.getElementById('get-app-screen');
  getAppButton = document.getElementById('get-app-button');
  getAppDescription = document.getElementById('get-app-description');

  // Add retry button handler
  retryButton.addEventListener('click', () => {
    callFirebaseFunction();
  });
  
  // Check if device is mobile
  const isMobileDevice = checkDeviceAndShowQR();
  
  // Only initialize Firebase and call function if on mobile device
  if (isMobileDevice) {
    try {
      initializeFirebase();
      
      // Call Firebase function on page load
      callFirebaseFunction();
    } catch (error) {
      showError(error);
    }
  }
}

// Wait for DOM to be ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}